/**
 * Root route server state: the per-request payload the root `beforeLoad`
 * produces on the server (session flag, consent cookie, dehydrated atoms) and
 * the client-side cache that stops the `fetchServerState` server function from
 * being called again on every client navigation and intent preload.
 *
 * Why a cache is safe: after hydration the client never needs this payload
 * again for as long as the visitor's auth state is unchanged — the consent
 * banner seeds `cookieConsentAtom` once, and the cached value is the tab's own
 * SSR result, returned by reference so `HydrationBoundary`, which memoizes on
 * the `state` array identity, never re-hydrates a stale payload over live
 * atoms.
 *
 * Why the cache is keyed by the Clerk session: the payload carries the
 * request's auth (`serverAuth`, dehydrated user atoms). Clerk completes a
 * sign-in with a CLIENT-side navigation (`routerPush` to `redirect_url`), so
 * without a key the first protected page after sign-in would be served the
 * signed-out payload cached from `/login` and fail with
 * "User not authenticated" until a full reload. The cache therefore remembers
 * the Clerk session id it was captured under and is refetched once the live
 * session id differs (sign-out is a full-document navigation, which reseeds).
 *
 * Pure module: no React, no Clerk, no request context.
 *
 * @since 1.0.0
 */
import type { Hydration } from "@effect-atom/atom-react";
import { Effect } from "effect";
import type { CookieConsent } from "./atoms/cookie-consent.ts";

/**
 * Server state produced by the root `fetchServerState` server function.
 *
 * @since 1.0.0
 */
export interface BaseServerState {
  readonly serverAuth: {
    readonly userId: string | null;
    readonly sessionId: string | null;
  };
  readonly serverUser: null;
  /**
   * The visitor's persisted cookie-consent choice, resolved from the request
   * cookie server-side (`null` = undecided).
   */
  readonly consent: CookieConsent | null;
  readonly dehydratedAtoms: Array<Hydration.DehydratedAtom>;
}

/**
 * The neutral state used when nothing has been fetched or cached yet.
 *
 * @since 1.0.0
 */
export const EMPTY_SERVER_STATE: BaseServerState = {
  serverAuth: { userId: null, sessionId: null },
  serverUser: null,
  consent: null,
  dehydratedAtoms: [],
};

/**
 * Per-tab cache of the SSR server state (module scope in the client bundle).
 * Seeded by {@link seedClientServerState} from the hydrated root context and
 * refreshed by {@link resolveRootServerState} whenever the client does fetch.
 *
 * @since 1.0.0
 */
export const clientServerStateRef: { current: BaseServerState | null } = { current: null };

/**
 * The Clerk session id the cached state was captured under (`null` = signed
 * out or Clerk not yet loaded). Compared against {@link readClientSessionId}
 * on every client navigation; a difference invalidates the cache.
 *
 * @since 1.0.0
 */
export const clientSessionKeyRef: { current: string | null } = { current: null };

/**
 * The live Clerk session id on the client, read from the `Clerk` global the
 * Clerk script installs on `window` — so this pure module never imports Clerk.
 * `null` when there is no window, Clerk has not loaded, or nobody is signed in.
 * `win` is injectable for tests.
 *
 * @since 1.0.0
 */
export const readClientSessionId = (
  win: unknown = typeof window === "undefined" ? undefined : window,
): string | null => {
  if (typeof win !== "object" || win === null) return null;
  const clerk: unknown = Reflect.get(win, "Clerk");
  if (typeof clerk !== "object" || clerk === null) return null;
  const session: unknown = Reflect.get(clerk, "session");
  if (typeof session !== "object" || session === null) return null;
  const id: unknown = Reflect.get(session, "id");
  return typeof id === "string" ? id : null;
};

/**
 * Inputs to the fetch decision.
 *
 * @since 1.0.0
 */
export interface RootServerStateInput {
  readonly isServer: boolean;
  readonly preload: boolean;
  readonly hasCached: boolean;
  /** The live Clerk session differs from the one the cache was captured under. */
  readonly sessionChanged?: boolean;
}

/**
 * Fetch on the server always; on the client only for a real (non-preload)
 * navigation with nothing cached yet — or whose cache belongs to a different
 * Clerk session (the visitor signed in since it was captured).
 *
 * @since 1.0.0
 */
export const shouldFetchServerState = (input: RootServerStateInput): boolean =>
  input.isServer || (!input.preload && (!input.hasCached || input.sessionChanged === true));

/**
 * Seed the client cache from the hydrated root context on first client render.
 * No-op on the server and once the cache is populated.
 *
 * @since 1.0.0
 */
export const seedClientServerState = (
  state: BaseServerState,
  sessionId: string | null = readClientSessionId(),
): void => {
  if (typeof window === "undefined") return;
  if (clientServerStateRef.current !== null) return;
  clientServerStateRef.current = state;
  clientSessionKeyRef.current = sessionId;
};

/**
 * Resolve the root server state for one `beforeLoad` run. Fetches (and, on
 * the client, caches) when {@link shouldFetchServerState} says so; otherwise
 * returns the cached object ITSELF (same reference) or the neutral state.
 *
 * @since 1.0.0
 */
export const resolveRootServerState = (
  input: { readonly isServer: boolean; readonly preload: boolean },
  fetch: () => Promise<BaseServerState>,
  readSessionId: () => string | null = readClientSessionId,
): Promise<BaseServerState> => {
  const cached = clientServerStateRef.current;
  const sessionId = input.isServer ? null : readSessionId();
  const sessionChanged = !input.isServer && cached !== null && sessionId !== clientSessionKeyRef.current;
  if (shouldFetchServerState({ ...input, hasCached: cached !== null, sessionChanged })) {
    return Effect.runPromise(
      Effect.promise(fetch).pipe(
        Effect.tap((state) =>
          Effect.sync(() => {
            if (!input.isServer) {
              clientServerStateRef.current = state;
              clientSessionKeyRef.current = sessionId;
            }
          })
        ),
      ),
    );
  }
  return Promise.resolve(cached ?? EMPTY_SERVER_STATE);
};
